<template>
  <div>
    <Spinner v-if="loading" />
    <p v-if="!userHasReadPermission(PermissionTypes.TaskPermissionTypes.task)">
      Pickup Tasks not accessible
    </p>
    <div v-if="!loading && userHasReadPermission(PermissionTypes.TaskPermissionTypes.task)">
      <p class="text-danger font-weight-bold" v-if="loadingError">{{loadingError}}</p>
      <div v-if="isPendingPickup">
        <div v-if="!hasTasks">No tasks assigned to this purchase order for pickup</div>
      </div>
      <div v-if="hasTasks">
        <p>Tasks associated with this purchase order:</p>
        <ItemPanelList class="mt-2">
          <ItemPanel v-for="task of linkedTasks" :key="task.id" :icon="taskIcon(task)">
            <template v-slot:title>
              <strong>
                <LinkTask :id="task.id" :name="getTaskTypeName(task)" />
              </strong>
            </template>
            <template v-slot:description>
              {{task.sequenceNumber}}
            </template>
            <template v-slot:content>
              <ItemPanelData title="Name">
                {{taskTitle(task)}}
              </ItemPanelData>
              <ItemPanelData title="Status">
                <LabelBadge :labelClass="`label-task-status-${task.status}`">{{task.status | formatLookupName(TaskStatusTypeLookup)}}</LabelBadge>
              </ItemPanelData>
              <ItemPanelData title="Scheduled" v-if="task.status == TaskStatusTypes.pendingProduction || task.status == TaskStatusTypes.loading || task.status == TaskStatusTypes.ready">
                {{task.scheduleStartDate | formatDateTime}}
              </ItemPanelData>
              <ItemPanelData title="Started" v-if="task.status == TaskStatusTypes.inProgress && task.startedDate">
                {{task.startedDate | formatDateTime}}
              </ItemPanelData>
              <ItemPanelData title="Completed" v-if="task.status == TaskStatusTypes.completed">
                {{task.completedDate | formatDateTime}}
              </ItemPanelData>
              <ItemPanelData title="Cancelled" v-if="task.status == TaskStatusTypes.cancelled">
                {{task.cancelledDate | formatDateTime}}
              </ItemPanelData>
              <ItemPanelData title="Assigned">
                {{resourceDetailsForTask(task)}}
              </ItemPanelData>
              <ItemPanelData title="Estimated" v-if="isTaskLocationTracking(task) && (task.status == TaskStatusTypes.ready || task.status == TaskStatusTypes.loading || task.status == TaskStatusTypes.inProgress) && estimatedTaskItemDate(task)">
                {{formatDateTime(estimatedTaskItemDate(task))}}
              </ItemPanelData>
              <ItemPanelData title="Loading Notes" v-if="isTaskLoadingNotesRequired(task)">
                {{formatHyphenEmptyValue(task.loadingNotes)}}
              </ItemPanelData>
            </template>
          </ItemPanel>
        </ItemPanelList>
      </div>

      <div v-if="canCreateTask" class="mt-4">
        <ProgressTrackerButton v-for="taskType of linkedTaskTypeList" :key="taskType.id" @click="addTask(taskType)" :icon="getTaskTypeIcon(taskType)" class="mr-2">Add {{taskType.name}} Task</ProgressTrackerButton>
        <ProgressTrackerButton v-if="canMoveToReceiving" @click="goodsReceivedClicked" icon="fa-forward">Goods Received</ProgressTrackerButton>

      </div>
    </div>
  </div>
</template>


<script>
import _ from 'lodash'

import EventBus from '@/utils/eventBus'
import FormMixin from '@/mixins/form'
import TaskDespatchMixin from '@/modules/tasks/mixins/taskDespatchMixin'
import { TaskStatusTypes, TaskStatusTypeLookup, PurchaseOrderStatusTypes } from '@/store/types'

import ProgressTrackerButton from '@/components/layout/progressTrackerButton.vue'
import TaskNewDialog from '@/modules/tasks/views/taskNewDialog'

export default {
  name: 'PurchaseOrderDespatchTaskList',
  mixins: [FormMixin, TaskDespatchMixin],
  components: {
    ProgressTrackerButton
  },
  props: {
    purchaseOrder: Object,
    canCreateTask: Boolean,
    canMoveToReceiving: Boolean
  },
  data() {
    return {
      loading: false,
      loadingError: '',
      linkedTasks: [],
      taskTypeList: []
    }
  },
  watch: {
    'purchaseOrder.id': function() {
      this.reloadData()
    }
  },
  computed: {
    isPendingPickup() {
      return (this.purchaseOrder.status == PurchaseOrderStatusTypes.pendingPickup)
    },
    hasTasks() {
      return this.linkedTasks.length > 0
    },
    linkedTaskTypeList() {
      return this.taskTypeList.filter(taskType => {
        // Needs transfer enabled
        return taskType.isTransferEnabled
      })
    }
  },
  created() {
    this.TaskStatusTypes = TaskStatusTypes
    this.TaskStatusTypeLookup = TaskStatusTypeLookup
  },
  mounted() {
    EventBus.on(EventBus.EventTypes.permissionsUpdated, this.onPermissionsUpdated)

    this.requestData()
  },
  beforeDestroy() {
    EventBus.off(EventBus.EventTypes.permissionsUpdated, this.onPermissionsUpdated)
  },
  methods: {
    requestData() {
      this.loadingError = ''

      if(this.userHasReadPermission(this.PermissionTypes.TaskPermissionTypes.task)) {
        this.loading = true

        Promise.all([
          this.$apiService.tasksService.getTaskList({ filter: { purchaseOrderId: this.purchaseOrder.id } }),
          this.$apiService.tasksService.getTaskTypeList({ filter: { isActive: 1 } })
        ])
        .then(results => {
          this.linkedTasks = results[0].data
          this.taskTypeList = results[1].data
        })
        .catch(err => {
          this.loadingError = this.processFormResponse(err).errorMessage
        })
        .finally(() => {
          this.loading = false
        })
      }
    },
    onPermissionsUpdated() {
      this.reloadData()
    },
    reloadData() {
      this.requestData()
    },
    resourceDetailsForTask(task) {
      let outputList = []
      if(task.combinedTaskResourceList)
        outputList = _.concat(outputList, task.combinedTaskResourceList)
      if(task.combinedTaskUserList)
        outputList = _.concat(outputList, task.combinedTaskUserList)

      if(outputList.length > 0)
        return outputList.join(', ')

      return 'None'
    },
    isTaskLocationTracking(task) {
      return task.taskType.isLocationTrackingEnabled
    },
    isTaskLoadingNotesRequired(task) {
      return task.taskType.isLoadingNotesEnabled
    },
    estimatedTaskItemDate(task) {
      if(task.taskItems && task.taskItems.length > 0) {
        return task.taskItems[0].estimatedCompletionDate
      }
      return null
    },
    getTaskTypeName(task) {
      return task.taskType.name
    },
    taskTitle(task) {
      return task.name
    },
    taskIcon(task) {
      return this.getTaskTypeIcon(task.taskType)
    },
    addTask(taskType) {
      this.displayModal(TaskNewDialog, {
        taskTypeId: taskType.id,
        contactId: this.purchaseOrder.contactId,
        contactName: this.purchaseOrder.contact.name,
        purchaseOrderIdList: [this.purchaseOrder.id],
        accountLocationId: this.purchaseOrder.accountLocationId
      })
      .then((success) => {
        if(success) {
          this.successToast(`Task Added`)
          this.$emit('taskCreated')
        }
      })
    },
    goodsReceivedClicked() {
      this.$emit('goodsReceived')
    }

  }


}
</script>